import { useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useSelector }     from 'react-redux'
import ActivityKPIDashboardKpiCard from './ActivityKPIDashboardKpiCard'

const TOTAL_HEIGHT = 260 * 3  // 780px default total
const MIN_HEIGHT   = 120
const MAX_HEIGHT   = 600

function ResizeHandle({ onResize }) {
  const dragging = useRef(false)
  const startY   = useRef(0)
  const startH   = useRef(0)

  const onMouseDown = (e) => {
    dragging.current = true
    startY.current   = e.clientY
    startH.current   = onResize.currentH()

    const onMove = (e) => {
      if (!dragging.current) return
      const delta = e.clientY - startY.current
      const newH  = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, startH.current + delta))
      onResize.set(newH)
    }
    const onUp = () => {
      dragging.current = false
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup',   onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup',   onUp)
  }

  return (
    <Box
      onMouseDown={onMouseDown}
      sx={{
        height: 8, cursor: 'ns-resize', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        '&:hover .handle-line': { bgcolor: 'primary.main', opacity: 1 },
        userSelect: 'none',
      }}
    >
      <Box className="handle-line" sx={{
        width: 48, height: 3, borderRadius: 2,
        bgcolor: 'divider', opacity: 0.6,
        transition: 'all .15s',
      }} />
    </Box>
  )
}

export default function ActivityKPIDashboardKpiGrid({ showMarks = true, gridRef = null }) {
  const { tech1, tech2, kpiSel, numCharts = 3 } = useSelector(s => s.filters)
  const [cardH, setCardH] = useState(Math.floor(TOTAL_HEIGHT / 3))
  const cardHRef = useRef(cardH)
  cardHRef.current = cardH

  if (!tech1) return null

  const comparing = tech2 !== null
  const kpis1     = Array.from(kpiSel[tech1] ?? []).slice(0, numCharts)
  const kpis2     = tech2 ? Array.from(kpiSel[tech2] ?? []).slice(0, numCharts) : []

  return (
    <Box ref={gridRef} sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {kpis1.map((kpiName, slot) => (
        <Box key={slot}>
          <Box sx={{ display: 'flex', gap: 1.5, height: cardH, mb: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ActivityKPIDashboardKpiCard tech={tech1} kpiName={kpiName} slot={slot} showMarks={showMarks} />
            </Box>
            {comparing && (
              tech2
                ? <Box sx={{ flex: 1, minWidth: 0 }}>
                    {kpis2[slot] && <ActivityKPIDashboardKpiCard tech={tech2} kpiName={kpis2[slot]} slot={slot} showMarks={showMarks} />}
                  </Box>
                : <Box sx={{ flex: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {slot === 0 && <Typography variant="body2" color="text.disabled">Select a technology to compare</Typography>}
                  </Box>
            )}
          </Box>
          {/* Resize handle after each row except last */}
          {slot < kpis1.length - 1 && (
            <ResizeHandle onResize={{ currentH: () => cardHRef.current, set: setCardH }} />
          )}
        </Box>
      ))}
      {/* Resize handle at the bottom — resize last chart */}
      <ResizeHandle onResize={{ currentH: () => cardHRef.current, set: setCardH }} />
    </Box>
  )
}